import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);
const IDLE_MS = 5 * 60 * 1000;
const LAST_KEY = "last_activity";
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel", "click"];

export function AuthProvider({ children }) {
  // null = checking, false = not authed, object = authed
  const [user, setUser] = useState(null);
  const [idleOut, setIdleOut] = useState(false);
  const userRef = useRef(user);
  userRef.current = user;

  const checkAuth = useCallback(async () => {
    const last = Number(localStorage.getItem(LAST_KEY) || 0);
    if (localStorage.getItem("token") && last && Date.now() - last > IDLE_MS) {
      localStorage.removeItem("token"); localStorage.removeItem(LAST_KEY);
      try { await api.post("/auth/logout"); } catch (e) { console.debug("logout on idle failed", e); }
      setIdleOut(true); setUser(false); return;
    }
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem(LAST_KEY, String(Date.now()));
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  // Auto-logout after 5 minutes of inactivity (shared across tabs via localStorage)
  useEffect(() => {
    if (!user) return;
    let throttle = 0;
    const touch = () => { const n = Date.now(); if (n - throttle > 1000) { throttle = n; localStorage.setItem(LAST_KEY, String(n)); } };
    touch();
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, touch, { passive: true }));
    const timer = setInterval(async () => {
      const last = Number(localStorage.getItem(LAST_KEY) || 0);
      if (!userRef.current || !last || Date.now() - last < IDLE_MS) return;
      try { await api.post("/auth/logout"); } catch (e) { console.debug("logout on idle timer failed", e); }
      localStorage.removeItem("token"); localStorage.removeItem(LAST_KEY);
      setIdleOut(true); setUser(false);
      toast.warning("Signed out after 5 minutes of inactivity", { id: "idle-logout", duration: 8000 });
    }, 5000);
    return () => { ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, touch)); clearInterval(timer); };
  }, [user]);

  useEffect(() => {
    // Skip /me during Emergent OAuth callback — AuthCallback will handle it
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.access_token);
    localStorage.setItem(LAST_KEY, String(Date.now()));
    setIdleOut(false);
    setUser(data.user);
    return data.user;
  };

  const exchangeSession = async (session_id) => {
    const { data } = await api.post("/auth/session", { session_id });
    localStorage.setItem("token", data.access_token);
    localStorage.setItem(LAST_KEY, String(Date.now()));
    setIdleOut(false);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.debug("logout failed", e); }
    localStorage.removeItem("token");
    localStorage.removeItem(LAST_KEY);
    setUser(false);
  };

  const can = (perm) => !!user && (user.role === "admin" || (user.permissions || []).includes(perm));

  return (
    <AuthCtx.Provider value={{ user, setUser, login, logout, exchangeSession, checkAuth, can, idleOut }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
